import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Monitor } from 'lucide-react';
import { PLAY_STORE_URL, WEB_APP_URL } from '../config/urls';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Features', path: '/features' },
    { name: 'Demo', path: '/demo' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Contact', path: '/contact' },
  ];

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-gray-900/95 backdrop-blur-xl shadow-lg border-b border-gray-700/20' 
          : 'bg-gray-900/90 backdrop-blur-xl border-b border-gray-700/20'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="container">
        <div className="flex items-center justify-between min-h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 text-white font-bold text-xl transition-all duration-300 hover:scale-105">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden shadow-lg shadow-primary-500/30">
              <img 
                src="/app_icon_512.png" 
                alt="Wellness Pro" 
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-gradient">Wellness Pro</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative font-medium transition-all duration-300 hover:text-primary-300 hover:-translate-y-0.5 ${
                  location.pathname === item.path 
                    ? 'text-primary-300 font-semibold' 
                    : 'text-white'
                }`}
              >
                {item.name}
                {location.pathname === item.path && (
                  <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Buttons - same size badge style */}
          <div className="hidden md:flex items-center gap-3">
            <a 
              href={WEB_APP_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-2 rounded-lg hover:from-emerald-500 hover:to-teal-500 shadow-md shadow-emerald-500/20 transition-all duration-300 hover:scale-105 min-h-[40px]"
            >
              <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-md bg-white/15 p-0.5" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
                  <rect x="2" y="3" width="20" height="13" rx="1.5" />
                  <path d="M7 20h10" />
                  <path d="M12 20v2" />
                  <path d="M5 7h14" />
                  <circle cx="8" cy="5.5" r="0.8" fill="currentColor" />
                  <circle cx="11" cy="5.5" r="0.8" fill="currentColor" />
                  <circle cx="14" cy="5.5" r="0.8" fill="currentColor" />
                </svg>
              </span>
              <div className="flex flex-col items-start">
                <span className="text-[9px] leading-tight uppercase tracking-wide opacity-90">Open in</span>
                <span className="text-sm font-bold leading-tight">Web App</span>
              </div>
            </a>
            <a 
              href={PLAY_STORE_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40 min-h-[40px]"
            >
              <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div className="flex flex-col items-start">
                <span className="text-[9px] leading-tight">GET IT ON</span>
                <span className="text-sm font-bold leading-tight">Google Play</span>
              </div>
            </a>
            <span className="text-white text-xs font-medium whitespace-nowrap">30 Days Free Trial</span>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white hover:text-primary-300 hover:bg-white/10 rounded-lg transition-all duration-300"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="md:hidden overflow-hidden bg-white border-t border-gray-200 shadow-lg"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="py-8 flex flex-col gap-6">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={item.path}
                      className={`block py-3 px-4 font-medium transition-all duration-300 ${
                        location.pathname === item.path 
                          ? 'text-primary-500 font-semibold bg-gradient-to-r from-primary-50 to-transparent pl-6' 
                          : 'text-gray-700 hover:text-primary-500 hover:pl-6'
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="pt-4 border-t border-gray-200 space-y-3"
                >
                  <a
                    href={WEB_APP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/25 transition-all duration-300"
                    onClick={() => setIsOpen(false)}
                  >
                    <Monitor className="w-6 h-6" />
                    Launch Web App
                  </a>
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-6 py-4 rounded-xl hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40 w-full"
                    onClick={() => setIsOpen(false)}
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="flex flex-col items-start">
                      <span className="text-[10px] leading-tight">GET IT ON</span>
                      <span className="text-lg font-bold leading-tight">Google Play</span>
                    </div>
                  </a>
                  <p className="text-center text-gray-600 text-sm font-medium">30 Days Free Trial</p>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
